import { FC, Fragment, ReactElement, useCallback, useEffect, useState } from 'react';
import { CmsProduct, ShopContent, Subcategory, CmsVirtualProduct } from '../../../types/cms_shared_types';
import { ProductCard } from './ProductCard';
import { useFormikContext } from 'formik';
import { ProductChoiceFormInput } from './ProductChoice';
import { Product, ProductListActions } from '../../../types/webshop';
import { ExtraProductDrawer } from './ExtraProductDrawer';
import { Conditional } from '../../Common/Conditional';
import { ProductConfigurationModal } from './ProductConfigurationModal/ProductConfigurationModal';
import * as Sentry from '@sentry/react';
import { CardContentType, getCardContent } from './utils';
import { getOrderedDisplayItems } from './ordering';

const SHOW_ALL_PRODUCTS_THRESHOLD = 4;

interface ProductChoiceListProps {
  selectedSubcategory: Subcategory | undefined;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  cmsShopContent: ShopContent;
  monthlyInvoicedAddonsInCart?: boolean;
  removeAddons?: () => void;
}

export const ProductChoiceList: FC<ProductChoiceListProps> = ({
  selectedSubcategory,
  selectedProducts,
  selectedProductsActions,
  cmsShopContent,
  monthlyInvoicedAddonsInCart,
  removeAddons,
}) => {
  const [productsOnDisplay, setProductsOnDisplay] = useState<CmsProduct[]>([]);
  const [productsInExtra, setProductsInExtra] = useState<CmsProduct[]>([]);
  const [virtualProductsOnDisplay, setVirtualProductsOnDisplay] = useState<CmsVirtualProduct[]>([]);
  const { values, validateForm } = useFormikContext<ProductChoiceFormInput>();

  const [showProductConfigurationModal, setShowProductConfigurationModal] = useState(false);
  const [showExtraProductsDrawer, setShowExtraProductsDrawer] = useState(false);

  const [currentProduct, setCurrentProduct] = useState<CmsProduct>();
  const [currentVirtualProduct, setCurrentVirtualProduct] = useState<CmsVirtualProduct>();

  useEffect(() => {
    validateForm(values);
  }, [selectedProducts, validateForm, values]);

  useEffect(() => {
    if (!selectedSubcategory) {
      return;
    }

    if (
      selectedProducts.some(
        (p) =>
          ![
            ...selectedSubcategory.products,
            ...selectedSubcategory.extraProducts,
            ...(selectedSubcategory.virtualProducts?.flatMap((vp) => vp.products) ?? []),
          ].some((pp) => pp.code === p.code),
      )
    ) {
      Sentry.captureMessage(
        `There is a mismatch between the selected products found in local storage and the products available in '${selectedSubcategory.key}'. This can occur if the subcategory was modified in the local storage state. To fix this, we're going to reset the list of selected products.`,
      );
      selectedProductsActions.reset();
    }

    setVirtualProductsOnDisplay(selectedSubcategory.virtualProducts || []);

    const newProductsOnDisplay: CmsProduct[] = selectedSubcategory.products.filter((p) => {
      // Sometimes, the application can find itself in a state where an extra product will also be in the "main" products.
      // To fix this, we simply consider the "main" products as the ones that aren't also in extra products.
      return !selectedSubcategory.extraProducts.some((pp) => pp.code === p.code);
    });
    const newProductsInExtra: CmsProduct[] = [];

    selectedSubcategory.extraProducts.forEach((extraProduct) => {
      if (selectedProducts.some((p) => p.code === extraProduct.code)) {
        if (!newProductsOnDisplay.includes(extraProduct)) {
          newProductsOnDisplay.push(extraProduct);
        }
      } else {
        newProductsInExtra.push(extraProduct);
      }
    });

    if (newProductsOnDisplay.length <= SHOW_ALL_PRODUCTS_THRESHOLD) {
      setProductsOnDisplay([...newProductsOnDisplay, ...newProductsInExtra]);
      setProductsInExtra([]);
    } else {
      setProductsOnDisplay(newProductsOnDisplay);
      setProductsInExtra(newProductsInExtra);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategory]);

  const moveSelectedExtraProductsToDisplayList = useCallback(() => {
    const newProductsOnDisplay: CmsProduct[] = [...productsOnDisplay];
    const newProductsInExtra: CmsProduct[] = [];

    const selectedExtraProducts = productsInExtra.filter((p) => selectedProducts.some((pp) => pp.code === p.code));

    selectedExtraProducts.forEach((prod: CmsProduct) => {
      newProductsOnDisplay.push(prod);
    });

    productsInExtra.forEach((prod: CmsProduct) => {
      if (!selectedExtraProducts.includes(prod)) {
        newProductsInExtra.push(prod);
      }
    });

    setProductsOnDisplay(newProductsOnDisplay);
    setProductsInExtra(newProductsInExtra);
  }, [productsInExtra, productsOnDisplay, selectedProducts]);

  useEffect(() => {
    const allExtraProductsHaveBeenAdded = productsInExtra.every((p) =>
      selectedProducts.find((pp) => pp.code === p.code),
    );
    if (showExtraProductsDrawer && allExtraProductsHaveBeenAdded) {
      moveSelectedExtraProductsToDisplayList();
    }
  }, [moveSelectedExtraProductsToDisplayList, productsInExtra, selectedProducts, showExtraProductsDrawer]);

  const displayProduct = (prod: CmsProduct, isWithinGroup = false, virtualProduct?: CmsVirtualProduct) => {
    return (
      <ProductCard
        key={prod.code}
        product={prod}
        selectedProducts={selectedProducts}
        selectedProductsActions={selectedProductsActions}
        openProductConfigurationModal={() => {
          setCurrentProduct(prod);
          setCurrentVirtualProduct(virtualProduct);
          setShowProductConfigurationModal(true);
        }}
        cmsShopContent={cmsShopContent}
        isWithinGroup={isWithinGroup}
        virtualProduct={virtualProduct}
      />
    );
  };

  const displayProducts = (products: CmsProduct[]): ReactElement => {
    const cardContentList = getCardContent(products);

    return (
      <>
        {cardContentList.map((cardContent) => {
          if (cardContent.type === CardContentType.GROUP) {
            const groupProducts = cardContent.products;
            const productGroupIdentifier = cardContent.identifier;
            const anySelectedProducts = groupProducts.some((product) =>
              selectedProducts.find((p) => p.code === product.code),
            );
            // Only show the identifier if the format isn't "(<identifier text>)", this is explained in Storyblok
            const showIdentifier = !(productGroupIdentifier.startsWith('(') && productGroupIdentifier.endsWith(')'));
            return (
              <div
                key={productGroupIdentifier}
                className={`${
                  anySelectedProducts ? 'border border-sami-green' : 'border border-sami-card-border'
                } flex flex-col pt-2 mb-4 rounded-lg shadow hover:shadow-md`}
              >
                {showIdentifier && <div className="py-2 pl-6">{productGroupIdentifier}</div>}
                {groupProducts.map((prod) => displayProduct(prod, true))}
              </div>
            );
          } else {
            return displayProduct(cardContent.product);
          }
        })}
      </>
    );
  };

  const displayMainItemsPreservingOrder = (): ReactElement[] => {
    const sortedItems = getOrderedDisplayItems({
      products: productsOnDisplay,
      virtualProducts: virtualProductsOnDisplay,
      extraProducts: selectedSubcategory?.extraProducts ?? [],
    });

    const content: ReactElement[] = [];
    let index = 0;

    while (index < sortedItems.length) {
      const item = sortedItems[index];

      if (item.type === 'virtual') {
        content.push(
          <Fragment key={`virtual-${item.virtualProduct.name}-${index}`}>
            {displayProduct(item.virtualProduct.templateProduct, false, item.virtualProduct)}
          </Fragment>,
        );
        index += 1;
        continue;
      }

      const productRun: CmsProduct[] = [];
      const runStart = index;

      while (index < sortedItems.length) {
        const current = sortedItems[index];

        if (current.type !== 'product') {
          break;
        }

        productRun.push(current.product);
        index += 1;
      }

      content.push(<Fragment key={`product-run-${runStart}`}>{displayProducts(productRun)}</Fragment>);
    }

    return content;
  };

  return (
    <div className="w-full sm:max-w-[600px] sm:w-fit flex flex-col items-center">
      {displayMainItemsPreservingOrder()}

      <Conditional
        condition={productsInExtra.length > 0}
        trueRender={
          <ExtraProductDrawer
            showExtraProductsDrawer={showExtraProductsDrawer}
            setShowExtraProductsDrawer={(value) => {
              if (!value) {
                moveSelectedExtraProductsToDisplayList();
              }
              setShowExtraProductsDrawer(value);
            }}
            productsInExtra={productsInExtra}
            displayProductsFunc={displayProducts}
            cmsShopContent={cmsShopContent}
          />
        }
      />

      {currentProduct && (
        <Conditional
          condition={showProductConfigurationModal}
          trueRender={
            <ProductConfigurationModal
              cmsProduct={currentProduct}
              showModal={showProductConfigurationModal}
              setShowModal={setShowProductConfigurationModal}
              selectedProducts={selectedProducts}
              selectedProductsActions={selectedProductsActions}
              cmsShopContent={cmsShopContent}
              lastProductResetConfirmation={false}
              monthlyInvoicedAddonsInCart={monthlyInvoicedAddonsInCart}
              removeAddons={removeAddons}
              virtualProduct={currentVirtualProduct}
            />
          }
        />
      )}
    </div>
  );
};
